import { useRef, type TouchEvent } from 'react'

/**
 * Naar links of rechts vegen.
 *
 * Voor de fotoviewer: op een telefoon is vegen wat je vanzelf doet, en de
 * pijltjes links en rechts zijn een muisgebaar dat daar met je duim niet te
 * halen is.
 *
 * Met touch-events en niet met een bibliotheek: dit is vijftien regels, en het
 * enige wat er moeilijk aan is zijn de twee drempels hieronder.
 */
interface SwipeHandlers {
  onTouchStart: (event: TouchEvent) => void
  onTouchEnd: (event: TouchEvent) => void
}

/** Hoever je horizontaal moet halen voor het telt. Minder is per ongeluk. */
const MinimumDistance = 55

/**
 * Hoeveel schuiner dan horizontaal het nog mag zijn.
 *
 * Zonder deze grens veegt iemand die verticaal langs een staande foto strijkt
 * ook de volgende foto binnen. Een halve zo grote verticale beweging als
 * horizontale is nog duidelijk een zijwaartse veeg.
 */
const MaximumDrift = 0.5

export function useSwipe(onLeft: () => void, onRight: () => void): SwipeHandlers {
  // In een ref en niet in state: hiertussen hoeft er niets opnieuw getekend te
  // worden, en dat zou midden in een veeg juist storen.
  const start = useRef<{ x: number; y: number } | null>(null)

  return {
    onTouchStart: (event) => {
      const touch = event.changedTouches[0]

      // Twee vingers is knijpen om in te zoomen, geen veeg.
      start.current =
        event.touches.length === 1 && touch ? { x: touch.clientX, y: touch.clientY } : null
    },

    onTouchEnd: (event) => {
      const from = start.current
      const touch = event.changedTouches[0]

      start.current = null

      if (!from || !touch) {
        return
      }

      const dx = touch.clientX - from.x
      const dy = touch.clientY - from.y

      if (Math.abs(dx) < MinimumDistance || Math.abs(dy) > Math.abs(dx) * MaximumDrift) {
        return
      }

      // Naar links vegen brengt wat rechts stond in beeld: de foto's schuiven
      // mee met je vinger, zoals bij elke fotoapp.
      if (dx < 0) {
        onLeft()
      } else {
        onRight()
      }
    },
  }
}
